import { RouteComponentProps } from '@reach/router';
import React from 'react';
import { Helmet } from 'react-helmet';
import { connect, MapStateToProps } from 'react-redux';
import {
  updateTagAction,
  deleteTag,
  requireProviderTags,
  restoreTag,
  updateTag,
} from 'store/providers/providerSlice';
import { appDispatch, IProviders, RootState } from 'store/store';
import { display } from 'components/ErrorView/ErrorDialog';
import DataItem from 'components/FileManager/DataClass/DataItem';
import MultiList from 'components/FileManager/MultiList';
import notify from 'components/Notify';
import protectedRoute from 'components/protectedRoute';
import MoveClipboard from 'containers/Profile/Panels/DocumentManager/MoveClipboard';
import LogoLoading from '../../components/LogoLoading/LogoLoading';
import { notifyError } from '../../components/Notify/Notify';
import messages from './Messages';
import TagHeader from './TagHeader';
import TagView from './TagView';
import { ITags, ITagView } from 'store/providers/providerTypes';
import cloneDeep from 'lodash/cloneDeep';

type StateToProps = {
  tags: ITags['data'];
  provider: IProviders['map'][0];
  readOnlyTagIds: Set<number | undefined>;
};

type ParamProps = {
  providerId: number;
  tagPath: string;
};

type OwnProps = RouteComponentProps<ParamProps>;

type ComponentProps = OwnProps & StateToProps;

interface IOpenFolders {
  type: 'search' | 'folder' | 'tag';
  value: number | string;
}

interface IComponentStates {
  loading: boolean;
  openFolders: IOpenFolders[];
  openItems: any[];
  openItemId: string;
  moveItems: any[];
  view: string;
  showDeletedTags: boolean;
}

class TagsContainer extends React.Component<ComponentProps, IComponentStates> {
  state: IComponentStates = {
    loading: true,
    openFolders: [{ type: 'tag', value: 0 }],
    openItems: [],
    openItemId: '',
    moveItems: [],
    view: 'tags',
    showDeletedTags: false,
  };

  componentDidUpdate(prevProps: Readonly<ComponentProps>) {
    if (prevProps.tagPath !== this.props.tagPath) this.setFoldersFromUrl();
  }

  componentDidMount() {
    const providerId = Number(this.props.providerId);
    this.setFoldersFromUrl();
    appDispatch(requireProviderTags({ providerId }))
      .then(() => {
        this.setState({ loading: false });
      })
      .catch((err: any) => {
        display(err);
      });
  }

  private setFoldersFromUrl() {
    const openTags =
      this.props.tagPath
        ?.split(';')
        .map((it) => ({ type: 'tag' as any, value: Number(it) })) || [];
    let showDeleted = false;
    const { tags } = this.props;
    if (
      openTags.find((it) => {
        return tags?.[it.value].deleted;
      })
    )
      showDeleted = true;
    this.setState({
      openFolders: [{ type: 'tag', value: 0 }, ...openTags],
      showDeletedTags: this.state.showDeletedTags || showDeleted,
    });
  }

  handleMoveItem = (item: ITagView) => {
    const exists = this.state.moveItems.find((i) => i.data.id === item.id);
    if (item && !exists) {
      const newItem: any = {};
      newItem.data = new DataItem(item.id, null, null, null, null, item.text);
      newItem.data.type = 'tag';
      this.setState({ moveItems: [...this.state.moveItems, newItem] });
    }
  };

  handleDeleteItem = (item: ITagView) => {
    return appDispatch(
      deleteTag({ tag: item, providerId: this.props.providerId! })
    ).catch((err: any) => {
      notifyError(err);
      return Promise.reject(err);
    });
  };

  handleRestoreItem = (item: ITagView) => {
    return appDispatch(
      restoreTag({ tag: item, providerId: this.props.providerId! })
    ).catch((err: any) => {
      notifyError(err);
      return Promise.reject(err);
    });
  };

  handleCancelMove = (tagId: number) => {
    if (tagId) {
      const cloned = this.state.moveItems.filter(
        (item) => item.data.id !== tagId
      );
      this.setState({ moveItems: cloned });
    } else {
      this.setState({ moveItems: [] });
    }
  };

  handlePasteItem = (targetId: number) => {
    const promiseList: any[] = [];
    const fileNames: string[] = [];
    this.state.moveItems.forEach((moveItem) => {
      const tag: ITagView = {
        id: moveItem.data.id,
        text: moveItem.data.name,
        parentId: targetId === 0 ? null : targetId,
      };
      fileNames.push(tag.text);
      promiseList.push(
        appDispatch(updateTag({ tag: tag, providerId: this.props.providerId! }))
      );
    });

    Promise.all(promiseList)
      .then(() => {
        const tag = this.props.tags[targetId];
        notify(messages.movedSuccessfully(fileNames, tag ? tag.text : 'null'));
        this.setState({ moveItems: [] });
      })
      .catch((err) => {
        display(err);
        const tag = this.props.tags[targetId];
        notify(
          messages.movedFailed(fileNames, tag ? tag.text : 'null'),
          'error'
        );
      });
  };

  handleLoadItemByDad = () => {
    // nothing to do. it's created because list component calls this function
  };

  handleOpenFolder = (listIndex: number, itemId: number) => {
    const newOpenDads = this.state.openFolders.slice(0, listIndex);
    newOpenDads.push({ type: 'tag', value: itemId });
    this.setState({
      openFolders: newOpenDads,
    });
  };

  handleDropItem = (itemId: number, _newOrder: number, newParentId: number) => {
    const tag = this.props.tags[itemId];
    const clonedTag = cloneDeep(tag);
    const readOnlyTagIds = this.props.readOnlyTagIds;
    if (
      (readOnlyTagIds.has(itemId) || readOnlyTagIds.has(newParentId)) &&
      clonedTag.parentId !== newParentId
    )
      return Promise.reject();
    if (clonedTag.parentId !== newParentId || _newOrder !== clonedTag.order) {
      // dropped in other lists
      const providerId = this.props.providerId;
      const oldParentId = clonedTag.parentId;
      const oldOrder = clonedTag.order;
      clonedTag.parentId = newParentId;
      clonedTag.order = _newOrder;
      appDispatch(
        updateTagAction({ providerId: providerId as number, tag: clonedTag })
      );
      return appDispatch(
        updateTag({ tag: clonedTag, providerId: this.props.providerId! })
      ).catch((err: any) => {
        clonedTag.parentId = oldParentId;
        clonedTag.order = oldOrder;
        appDispatch(
          updateTagAction({
            providerId: providerId as number,
            tag: clonedTag,
          })
        );
        return Promise.reject(err);
      });
    }
    return Promise.resolve();
  };

  getComponentFunction(item: ITagView) {
    return (listIndex: number, selected: boolean) => {
      return (
        <TagView
          dispatch={appDispatch}
          item={item}
          listIndex={listIndex}
          selected={selected}
          readOnly={this.props.readOnlyTagIds.has(Number(item.id))}
          onOpenItem={this.handleOpenItem}
          providerId={this.props.providerId}
          isTagExists={this.isTagExists}
          onMoveItem={() => this.handleMoveItem(item)}
          onDeleteItem={() => this.handleDeleteItem(item)}
          onRestoreItem={() => this.handleRestoreItem(item)}
          provider={this.props.providerId}
        />
      );
    };
  }

  getHeaderFunction = (item: ITagView) => {
    return () => {
      return (
        <TagHeader
          readOnly={this.props.readOnlyTagIds.has(Number(item.id))}
          setShowDeletedTags={(newVal: boolean) =>
            this.setState({ showDeletedTags: newVal })
          }
          showDeletedTags={this.state.showDeletedTags}
          dispatch={appDispatch}
          item={item}
          providerId={this.props.providerId}
        />
      );
    };
  };

  getEmptyFolderComponent() {
    return (
      <li
        className="collection-item avatar valign-wrapper grey-text"
        style={{ padding: 'initial' }}
      >
        <div className="full-width">
          <span className="title center-align" style={{ display: 'block' }}>
            {messages.hasNotSubTask}
          </span>
        </div>
      </li>
    );
  }

  handleOpenItem = (listIndex: number, itemId: number) => {
    const newOpenDads = this.state.openFolders.slice(0, listIndex);
    newOpenDads.push({ type: 'tag', value: itemId });
    this.setState({
      openFolders: newOpenDads,
    });
  };

  isTagExists = (tagTitle: string) => {
    return Object.values(this.props.tags).find((tag) => tag.text === tagTitle);
  };

  handleDeletedTags = () => {
    this.setState((prevState) => {
      return {
        ...prevState,
        showDeletedTags: !this.state.showDeletedTags,
      };
    });
  };

  render() {
    const { tags, provider } = this.props;
    if (!provider || provider.isLoading || this.state.loading) {
      return <LogoLoading />;
    }

    const rootTag: ITagView = { id: 0, text: '', parentId: null };
    const listItems: any = {
      0: new DataItem(
        rootTag.id,
        rootTag.parentId,
        undefined,
        this.getComponentFunction(rootTag),
        this.getHeaderFunction(rootTag),
        rootTag.text,
        this.getEmptyFolderComponent as any
      ),
    };

    if (tags) {
      Object.values(tags).forEach((tag: ITagView) => {
        let order = tag.order || 0;
        if (!this.state.showDeletedTags && tag.deleted) return;
        if (order === 0 && typeof tag.id === 'number') {
          order = tag.id;
        }
        const dataItem: any = new DataItem(tag.id, tag.parentId || 0, order);
        dataItem.getComponent = this.getComponentFunction(tag);
        dataItem.getHeader = this.getHeaderFunction(tag);
        dataItem.getEmptyFolderComponent = this.getEmptyFolderComponent;
        dataItem.name = tag.text;
        // dataItem.isEditable = !readOnlyTagIds.has(tag.id);
        listItems[tag.id] = dataItem;
      });
    }

    return (
      <div style={{ height: '100%' }}>
        <Helmet>
          <title>{messages.tags}</title>
          <meta name="description" content={messages.tags} />
        </Helmet>
        <div className="row" style={{ height: '100%' }}>
          <div style={{ height: '100%' }}>
            {this.state.moveItems.length > 0 && (
              <MoveClipboard
                moveItems={this.state.moveItems}
                handleCancelMove={this.handleCancelMove}
              />
            )}
            <MultiList
              openFolders={this.state.openFolders}
              onLoadItemsByDad={this.handleLoadItemByDad}
              listItems={listItems}
              onOpenFolder={this.handleOpenFolder}
              onDrop={this.handleDropItem}
              type={'tag'}
              tags={tags}
              providerId={provider.data!.id}
              showTags={this.state.showDeletedTags}
              ShowDeletedTagsHandler={this.handleDeletedTags}
              isAdmin={true}
            />
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps: MapStateToProps<StateToProps, OwnProps, RootState> = (
  state,
  props
) => {
  const providerId = Number(props.providerId);

  const tags = state.providers.tags[providerId]?.data;
  const provider = state.providers.map[providerId];
  const thisUser = state.thisUser;
  const company =
    state.companies.data[Number(provider.data!.managingCompanyId!)];
  const isOwener = provider.data!.user === Number(thisUser.userId);
  const readOnlyIds =
    (company?.id &&
      Object.values(company.roles)
        ?.filter((it) => it.roleType === 'District' && it.relatedTagId)
        .map((it) => it.relatedTagId)) ||
    [];
  return {
    tags: tags || {},
    provider,
    thisUser,
    providerId,
    readOnlyTagIds: isOwener ? new Set() : new Set(readOnlyIds),
  };
};

export default protectedRoute(connect(mapStateToProps)(TagsContainer));
